/**
 * UTM Tracking Utility
 * Captures and persists marketing attribution parameters
 */

const UTM_STORAGE_KEY = 'utm_attribution';
const UTM_EXPIRY_DAYS = 30;

/**
 * Capture all tracking parameters from current URL
 */
export const captureUTMParameters = () => {
  console.log('🔍 Capturing UTM from URL:', window.location.href);
  const params = new URLSearchParams(window.location.search);
  console.log('📊 URL Search Params:', window.location.search);

  const attribution = {
    // UTM Parameters
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_term: params.get('utm_term'),
    utm_content: params.get('utm_content'),

    // Click IDs
    fbclid: params.get('fbclid'),
    gclid: params.get('gclid'),

    // Page tracking
    referrer_url: document.referrer || null,
    landing_page_url: window.location.href,

    // Device info
    device_info: {
      user_agent: navigator.userAgent,
      browser: getBrowserName(),
      browser_version: getBrowserVersion(),
      os: getOSName(),
      device_type: getDeviceType(),
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    },

    // Timestamp
    captured_at: new Date().toISOString(),
    expires_at: new Date(Date.now() + (UTM_EXPIRY_DAYS * 24 * 60 * 60 * 1000)).toISOString()
  };

  // Only store if we have at least one tracking parameter
  const hasTrackingData = attribution.utm_source ||
                          attribution.utm_campaign ||
                          attribution.fbclid ||
                          attribution.gclid;

  if (hasTrackingData) {
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(attribution));
    console.log('✅ UTM Parameters captured and stored:', {
      utm_source: attribution.utm_source,
      utm_campaign: attribution.utm_campaign,
      fbclid: attribution.fbclid,
      gclid: attribution.gclid
    });
  } else {
    console.log('ℹ️ No UTM tracking parameters found in URL');
  }

  return attribution;
};

/**
 * Get stored UTM parameters (if not expired)
 */
export const getStoredUTMParameters = () => {
  const stored = localStorage.getItem(UTM_STORAGE_KEY);

  if (!stored) return null;

  try {
    const attribution = JSON.parse(stored);
    const expiryDate = new Date(attribution.expires_at);

    if (expiryDate > new Date()) {
      return attribution;
    } else {
      // Expired, clear it
      localStorage.removeItem(UTM_STORAGE_KEY);
      console.log('⏰ UTM Parameters expired and cleared');
      return null;
    }
  } catch (error) {
    console.error('❌ Error parsing UTM data:', error);
    localStorage.removeItem(UTM_STORAGE_KEY);
    return null;
  }
};

/**
 * Clear stored UTM parameters
 */
export const clearUTMParameters = () => {
  localStorage.removeItem(UTM_STORAGE_KEY);
  console.log('🗑️ UTM Parameters cleared');
};

/**
 * Get UTM parameters formatted for API submission
 * Returns only the fields needed for lead creation
 */
export const getUTMForAPI = () => {
  const attribution = getStoredUTMParameters();

  if (!attribution) return {};

  return {
    utm_source: attribution.utm_source,
    utm_medium: attribution.utm_medium,
    utm_campaign: attribution.utm_campaign,
    utm_term: attribution.utm_term,
    utm_content: attribution.utm_content,
    fbclid: attribution.fbclid,
    gclid: attribution.gclid,
    referrer_url: attribution.referrer_url,
    landing_page_url: attribution.landing_page_url,
    device_info: attribution.device_info
  };
};

// Helper functions for device detection
const getBrowserName = () => {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Edge')) return 'Edge';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  return 'Unknown';
};

const getBrowserVersion = () => {
  const ua = navigator.userAgent;
  const match = ua.match(/(Chrome|Firefox|Safari|Edge|Opera|OPR)\/([0-9.]+)/);
  return match ? match[2] : 'Unknown';
};

const getOSName = () => {
  const ua = navigator.userAgent;
  if (ua.includes('Win')) return 'Windows';
  if (ua.includes('Mac')) return 'MacOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('like Mac') && ua.includes('Mobile')) return 'iOS';
  return 'Unknown';
};

const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobile))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};
